import { useCallback, useEffect, useRef } from 'react';
import { IBackgroundJob } from '@models/backgroundJobs/IBackgroundJob';
import { backgroundJobsStore } from '@services/backgroundJobs/BackgroundJobsStore';
import { selectActiveJobs } from '@services/backgroundJobs/backgroundJobsReducer';
import { BellJobFx, decideBellJobFx } from './NotificationBell/bellJobFx';

/** Пауза после возврата на вкладку перед отложенной сценой: глаз успевает вернуться к странице. */
export const DEFERRED_SIGNAL_DELAY_MS = 400;

export interface IJobOutcomeSignalHandlers {
    isDropdownOpen: () => boolean;
    play: (fx: BellJobFx, jobId: string) => void;
    /** Превью итога у колокольчика — только для успеха и отказа: отмену пользователь сделал сам. */
    showPreview: (job: IBackgroundJob) => void;
}

export interface IJobOutcomeSignal {
    /** Сигнал об итоге отложен до возврата на вкладку: превью уведомления о нём сейчас не нужно. */
    isDeferred: (jobId: string) => boolean;
}

/**
 * Сигнал об итоге фоновой задачи, которую клиент застал идущей: сцена колокольчика и превью итога — один сигнал,
 * после него итог считается объявленным, и уведомление о нём превью уже не даст. Итог, который уже перед глазами
 * (окно задачи, открытый колокольчик), не объявляется; скрытая вкладка получает сигнал, когда станет видимой, —
 * если итог к тому времени не скрыли и не показали окном. Отложенное живёт, пока не сменится пользователь.
 */
export const useJobOutcomeSignal = (
    userId: number | string,
    handlers: IJobOutcomeSignalHandlers,
): IJobOutcomeSignal => {
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;
    const deferredRef = useRef<ReadonlyMap<string, IBackgroundJob>>(new Map());

    useEffect(() => {
        const store = backgroundJobsStore;
        const deferred = new Map<string, IBackgroundJob>();
        deferredRef.current = deferred;
        /** Отложенный итог держит задачу в сторе: вытеснение закончившихся не должно забыть его до объявления. */
        const holds = new Map<string, () => void>();
        let flushTimer: number | null = null;

        const forget = (jobId: string) => {
            deferred.delete(jobId);
            holds.get(jobId)?.();
            holds.delete(jobId);
        };

        const signal = (job: IBackgroundJob) => {
            const { isDropdownOpen, play, showPreview } = handlersRef.current;
            const fx = decideBellJobFx({
                status: job.status,
                observed: store.isObserved(job.jobId),
                shown: store.wasAnnounced(job.jobId) || Boolean(store.getSnapshot().jobs[job.jobId]?.shownInWindow),
                dropdownOpen: isDropdownOpen(),
                tabVisible: document.visibilityState === 'visible',
                othersRunning: selectActiveJobs(store.getSnapshot()).length > 0,
            });
            if (fx === 'defer') {
                deferred.set(job.jobId, job);
                if (!holds.has(job.jobId)) holds.set(job.jobId, store.hold(job.jobId));
                return;
            }
            forget(job.jobId);
            store.markAnnounced(job.jobId);
            play(fx, job.jobId);
            if (fx !== 'none' && (job.status === 'succeeded' || job.status === 'failed')) showPreview(job);
        };

        const flush = () => {
            flushTimer = null;
            if (document.visibilityState !== 'visible') return;
            const { jobs } = store.getSnapshot();
            [...deferred.keys()].forEach((jobId) => {
                const tracked = jobs[jobId];
                if (tracked && tracked.dismissedAt === undefined && !tracked.shownInWindow) signal(tracked.job);
                else forget(jobId);
            });
        };

        const onVisibility = () => {
            if (document.visibilityState !== 'visible' || deferred.size === 0 || flushTimer !== null) return;
            flushTimer = window.setTimeout(flush, DEFERRED_SIGNAL_DELAY_MS);
        };

        const unsubscribe = store.onFinished(signal);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            unsubscribe();
            document.removeEventListener('visibilitychange', onVisibility);
            if (flushTimer !== null) window.clearTimeout(flushTimer);
            holds.forEach((release) => release());
        };
    }, [userId]);

    const isDeferred = useCallback((jobId: string) => deferredRef.current.has(jobId), []);
    return { isDeferred };
};
